import { Routes } from '@/src/common/enums/routes'
import { ACTIVATE_PAYMENT_CARD } from '@/src/common/gql/mutations/card'
import { useMutation } from '@apollo/client'
import { View, Text, ScrollView, StyleSheet } from 'react-native'
import { useRouter } from 'expo-router'
import { Button } from '@/src/common/components/Button'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { CardFront } from '@/src/app/main/(tabs)/card/card-management/virtual-card/CardFront'
import { cardImageStyle } from '@/src/app/main/(tabs)/card/card-management/virtual-card/cardImageStyle'
import { useLogError } from '@/src/common/hooks/useLogError'
import { GET_CREDIT_CARD_INFORMATION } from '@/src/common/gql/queries/card'
import { useLegacyCard } from '@/src/common/hooks/useCardInformation'
import { classes, mesaColorNames, typography } from '@/src/common/theme'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import type { ComponentProps, FunctionComponent } from 'react'
import { VirtualCardApi } from '@/src/experiences/virtual-card/store'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import { useEvent } from 'tamagui'

type ActivateCardComponent = FunctionComponent<{
  activateCard?: () => void
  loading?: boolean
  cardActivated?: boolean
}>

type WrappedActivateCardComponent = FunctionComponent<{
  ActivateCard: ActivateCardComponent
}>

function useAlertActivationError() {
  const Alert = useAlert(useAlertActivationError.name)
  return useEvent(() =>
    Alert.alert(
      'Error when trying to activate card',
      'There was an error while trying to activate your card - please try again later.',
    ),
  )
}

const LegacyActivateCard: WrappedActivateCardComponent = ({ ActivateCard }) => {
  const logError = useLogError()
  const alertActivationError = useAlertActivationError()
  const [activatePaymentCard, { loading }] = useMutation(ACTIVATE_PAYMENT_CARD, {
    refetchQueries: [GET_CREDIT_CARD_INFORMATION],
    awaitRefetchQueries: true,
  })
  const card = useLegacyCard().data?.getCreditCardInformation.node
  const cardActivated = card && !card.virtualCardIdForPhysicalCard

  return (
    <ActivateCard
      cardActivated={cardActivated ?? undefined}
      loading={loading}
      activateCard={() =>
        void activatePaymentCard({
          onError(error) {
            alertActivationError()
            logError(error)
          },
        })
      }
    />
  )
}

type CardAccountId = NonNullable<ReturnType<typeof useCardAccount>['data']>[number]['id']

const PlatformActivateCard: WrappedActivateCardComponent = ({ ActivateCard }) => {
  const query = useCardAccount()
  const cardAccountId = query.data?.[0]?.id
  return cardAccountId ? (
    <PlatformActivateCardWithAccountId ActivateCard={ActivateCard} cardAccountId={cardAccountId} />
  ) : (
    <ActivateCard loading />
  )
}

function PlatformActivateCardWithAccountId({
  ActivateCard,
  cardAccountId,
}: ComponentProps<typeof PlatformActivateCard> & { cardAccountId: CardAccountId }) {
  const logError = useLogError()
  const alertActivationError = useAlertActivationError()
  const [activateVirtualCard, { isLoading }] =
    VirtualCardApi.endpoints['POST /card/:cardAccountId/activate'].useMutation()
  const cardActivatedQuery = VirtualCardApi.endpoints['GET /card/:cardAccountId/virtual'].useQuery({
    params: { cardAccountId },
  })
  const cardActivated = cardActivatedQuery.data?.status === 'active'

  return (
    <ActivateCard
      loading={isLoading || cardActivatedQuery.isUninitialized || cardActivatedQuery.isLoading}
      activateCard={() => {
        void (async () => {
          try {
            const result = await activateVirtualCard({ params: { cardAccountId } })
            if (result.error) throw result.error
          } catch (error) {
            logError('Failed to activate virtual card', error)
            alertActivationError()
          }
        })()
      }}
      cardActivated={cardActivated}
    />
  )
}

export default provideCardPlatform<ActivateCardComponent>(({ ToWrap, platform }) => {
  switch (platform) {
    case 'HIGHNOTE':
      return <LegacyActivateCard ActivateCard={ToWrap} />
    case 'LOANPRO':
      return <PlatformActivateCard ActivateCard={ToWrap} />
    default:
      platform satisfies undefined
      return <ToWrap loading />
  }
})(function ActivateCard({ activateCard, cardActivated, loading }) {
  const router = useRouter()

  return (
    <ScrollView contentContainerStyle={styles.mainContainer}>
      <View
        style={{
          position: 'absolute',
          top: 0,
          height: cardImageStyle.height * 0.8,
          width: '100%',
          backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
        }}
      />
      <CardFront
        imageBackgroundStyle={{ ...cardImageStyle, ...classes.cardShadow }}
        style={classes.cardShadow}
      />
      {cardActivated ? (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>
            {' '}
            Your<Text style={styles.aquaText}> Mesa Homeowners card</Text> is now active
          </Text>
          <Text style={styles.cardBody}>Your card is now ready to be used!</Text>
          <Button
            onPress={() => router.dismissTo(Routes.CardManagement.route)}
            containerStyle={styles.activateButton}
          >
            <Text style={styles.modalButton}>Return Home</Text>
          </Button>
        </View>
      ) : (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>
            Activate your
            <Text style={styles.aquaText}> Mesa Homeowners card</Text>{' '}
          </Text>
          <Text style={styles.cardBody}>
            Once your physical card has arrived, tap below to activate it. Once activated, your card
            is now ready to be used!
          </Text>
          <Button onPress={activateCard} loading={loading} containerStyle={styles.activateButton}>
            <Text style={styles.modalButton}>Activate card</Text>
          </Button>
        </View>
      )}
    </ScrollView>
  )
})

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    zIndex: 1,
  },
  container: {
    flex: 1,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  aquaText: {
    ...typography.XL,
    marginBottom: 5,
    textAlign: 'center',
    paddingVertical: 10,
    color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
  },
  cardTitle: {
    ...typography.XL,
    marginTop: 30,
    marginBottom: 5,
    marginRight: 50,
    marginLeft: 50,
    alignItems: 'center',
    textAlign: 'center',
    color: '#000',
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  cardBody: {
    ...typography['paragraph/md'],
    color: mesaColorNames['global/grey/80 #2F3133'],
    textAlign: 'center',
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  activateButton: {
    ...typography['label/md-bold'],
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  buttonsContainer: {
    marginVertical: 20,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: 28,
  },
  buttonContainer: { display: 'flex', gap: 4, alignItems: 'center' },
  button: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
  },
  buttonText: { color: 'white', fontWeight: 700 },
  disclaimerText: { color: 'white', paddingHorizontal: 20, paddingVertical: 10, fontSize: 14 },
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    margin: 8,
  },
  modalText: {
    color: mesaColorNames['global/grey/80 #2F3133'],
    lineHeight: 24,
    textAlign: 'center',
  },
  modalButton: typography['label/md-bold'],
} as const)
